import { describeSchemaKind } from '../domain/schema';
import type { AnalysisResult, AnalysisWarning } from '../domain/models';
import type { ImportProgress as ImportProgressState } from '../workers/parserClient';

type ImportProgressProps = {
  progress?: ImportProgressState;
  result?: AnalysisResult | null;
  error?: string | null;
  warnings?: AnalysisWarning[];
};

export function ImportProgress({ progress, result, error, warnings = [] }: ImportProgressProps) {
  if (!progress && !result && !error && warnings.length === 0) return null;

  return (
    <section className="surface" style={{ padding: 16 }}>
      <div className="section">
        <div className="section-header">
          <div>
            <h2 className="section-title">Import Progress</h2>
            <p className="section-note">{progress?.stage ?? (result ? '解析結果' : 'エラー')}</p>
          </div>
          {progress ? <strong>{Math.round(progress.progress * 100)}%</strong> : null}
        </div>

        {progress ? (
          <div className="progress" aria-label="解析進捗">
            <span style={{ width: `${Math.round(progress.progress * 100)}%` }} />
          </div>
        ) : null}

        {result ? (
          <div className="metadata-grid">
            <div className="metadata-item">
              <span>ファイル</span>
              <strong>{result.fileName}</strong>
            </div>
            <div className="metadata-item">
              <span>スキーマ</span>
              <strong>{describeSchemaKind(result.schema)}</strong>
            </div>
            <div className="metadata-item">
              <span>読み込み点数</span>
              <strong>{result.points.length.toLocaleString('ja-JP')}</strong>
            </div>
            <div className="metadata-item">
              <span>除外点数</span>
              <strong>{(result.invalidPointCount + result.noisyPointCount).toLocaleString('ja-JP')}</strong>
            </div>
            <div className="metadata-item">
              <span>滞在候補</span>
              <strong>{result.visits.length.toLocaleString('ja-JP')}</strong>
            </div>
            <div className="metadata-item">
              <span>移動区間</span>
              <strong>{result.segments.length.toLocaleString('ja-JP')}</strong>
            </div>
            <div className="metadata-item">
              <span>日次集計</span>
              <strong>{result.dailySummaries.length.toLocaleString('ja-JP')}</strong>
            </div>
            <div className="metadata-item">
              <span>旅行候補</span>
              <strong>{result.trips.length.toLocaleString('ja-JP')}</strong>
            </div>
          </div>
        ) : null}

        <WarningList warnings={error ? [{ level: 'error', message: error }, ...warnings] : warnings} />
      </div>
    </section>
  );
}

export function WarningList({ warnings }: { warnings: AnalysisWarning[] }) {
  if (warnings.length === 0) return null;
  return (
    <div className="alert-list">
      {warnings.map((warning, index) => (
        <div key={`${warning.message}_${index}`} className={`alert ${warning.level}`}>
          {warning.message}
        </div>
      ))}
    </div>
  );
}
